import {
  Button,
  CollapseButton,
  DefaultScreenTileBox,
  DistanceBetween,
  Text,
  useToast,
} from '~/common/components/defaultUIComponents';
import {
  Box,
  Collapse,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Spinner,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import AutoEndWarningApi from '~/common/api/zeiterfassungServerApi/AutoEndWarningApi';
import LogoutWarningApi from '~/common/api/zeiterfassungServerApi/LogoutWarningApi';
import WorkingInfoApi from '~/common/api/zeiterfassungServerApi/WorkingInfoApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { DeleteIcon, EditIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { EmployeeWorkingHistory } from './EmployeeWorkingHistory';
import { secondsToFormatHMin } from '~/common/utils/timeUtils';
import React, { useCallback, useMemo, useState } from 'react';
import { DeleteEmployeeModal } from './DeleteEmployeeModal';
import { formatName } from '~/common/utils/employeeUtils';
import { EditEmployeeModal } from './EditEmployeeModal';
import useTranslate from '~/common/utils/translate';
import { useEmployeesContext } from './state';
import { useStore } from 'zustand';
import { format } from 'date-fns';
import moment from 'moment';
import axios from 'axios';

type Props = {
  employee: Employee;
};

export const EmployeeTile: React.FC<Props> = ({ employee }) => {
  const t = useTranslate();
  const toast = useToast({ status: 'error', description: 'generic.error' });
  const [loadingPdf, setLoadingPdf] = useState(false);

  const emplyoeeStore = useEmployeesContext();
  const { from, to, isOpen, onToggle } = useStore(emplyoeeStore, (state) => ({
    from: state.dateFrom,
    to: state.dateTo,
    isOpen: state.openEmployee?.id === employee.id,
    onToggle: () => state.toggleOpenEmployee(employee),
  }));
  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const workingInfosMap = WorkingInfoApi.hooks.useWorkingInfosMap(
    { employees: employees ?? [], from, to },
    {
      enabled: !!employees,
    },
  );
  const { isLoading } = WorkingInfoApi.hooks.useWorkingInfos(
    { employees: employees ?? [], from, to },
    {
      enabled: !!employees,
    },
  );

  const { data: autoEndWarnings } = AutoEndWarningApi.hooks.useEmployeeWarnings(
    {
      id: employee.id,
      start: from.valueOf(),
      end: to.valueOf(),
    },
  );

  const { data: logoutWarnings } = LogoutWarningApi.hooks.useEmployeeWarnings({
    id: employee.id,
    start: from.valueOf(),
    end: to.valueOf(),
  });

  const { isOpen: isEditEmployeeOpen, onToggle: toggleEditEmployeeOpen } =
    useDisclosure();
  const { isOpen: isDeleteEmployeeOpen, onToggle: toggleDeleteEmployeeOpen } =
    useDisclosure();
  const hasAutoEndWarning = !!autoEndWarnings?.length;
  const hasLogoutWarning = !!logoutWarnings?.length;
  const haveWarningsBeenSeen = useMemo(() => {
    const allWarnings = [...(autoEndWarnings || []), ...(logoutWarnings || [])];
    return (
      allWarnings.length > 0 &&
      allWarnings.every((warning) => warning.seen_by_admin === true)
    );
  }, [autoEndWarnings, logoutWarnings]);

  const employeeName = formatName(employee);
  const fetchPdf = useCallback(() => {
    setLoadingPdf(true);
    axios
      .post<Blob>(
        '/pdf/employees/',
        {
          employees: [employee.id],
          start: from.valueOf(),
          end: to.valueOf(),
          details: true,
        },
        { responseType: 'blob' },
      )
      .then((res) => {
        const fileName = `Arbeitszeiten_${format(from, 'yyyy_MM_dd')}_${format(
          to,
          'yyyy_MM_dd',
        )}.pdf`;
        const link = document.createElement('a');
        const href = URL.createObjectURL(res.data);
        link.href = href;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(href);
      })
      .catch(toast)
      .finally(() => setLoadingPdf(false));
  }, [employees, from, to]);

  return (
    <>
      <DefaultScreenTileBox distanceBetweenTiles={DistanceBetween.narrow}>
        <Grid templateColumns="repeat(3, 1fr)">
          <GridItem pt={2.5}>
            <Flex gap={2} alignItems="center" direction="row">
              {(hasAutoEndWarning || hasLogoutWarning) && (
                <Tooltip label={t('employee.hasWarning')}>
                  <Box cursor="pointer">
                    {haveWarningsBeenSeen ? (
                      <WarningTwoIcon color="green.600" height={5} />
                    ) : (
                      <WarningTwoIcon color="yellow.600" height={5} />
                    )}
                  </Box>
                </Tooltip>
              )}
              {isLoading && <Spinner />}
              <Text fontSize="md" fontWeight="semibold" mr={1}>
                {employeeName}
              </Text>
            </Flex>
          </GridItem>
          <GridItem>
            <Flex align="center">
              <CollapseButton
                isOpen={isOpen}
                toggleIsOpen={onToggle}
                ml={1}
                mr={2}
                isDisabled={isLoading}
              />
              <Text>
                {/* {summedWorkingHoursText} */}
                {`${secondsToFormatHMin(
                  moment
                    .duration(workingInfosMap?.[Number(employee.id)]?.is_time)
                    ?.asSeconds(),
                  true,
                )}`}
              </Text>
            </Flex>
          </GridItem>
          <GridItem>
            <Flex justifyContent="flex-end">
              <Button onClick={fetchPdf} isLoading={loadingPdf}>
                employeeTile.pdf.exportButton.text
              </Button>
              <Tooltip
                label={t('employeeTile.editEmployee.tooltip.label')}
                aria-label={t('employeeTile.editEmployee.tooltip.label')}
              >
                <IconButton
                  icon={<EditIcon />}
                  onClick={toggleEditEmployeeOpen}
                  aria-label={
                    t('employeeTile.editEmployee.tooltip.label') as string
                  }
                  isRound
                  mr={1}
                  ml={1}
                />
              </Tooltip>
              <Tooltip
                label={t('employeeTile.deleteEmployee.tooltip.label')}
                aria-label={t('employeeTile.deleteEmployee.tooltip.label')}
              >
                <IconButton
                  icon={<DeleteIcon />}
                  onClick={toggleDeleteEmployeeOpen}
                  aria-label={
                    t('employeeTile.deleteEmployee.tooltip.label') as string
                  }
                  isRound
                  mr={1}
                />
              </Tooltip>
            </Flex>
          </GridItem>
        </Grid>

        <Collapse in={isOpen}>
          {isOpen && <EmployeeWorkingHistory employee={employee} />}
        </Collapse>
      </DefaultScreenTileBox>
      <DeleteEmployeeModal
        isOpen={isDeleteEmployeeOpen}
        onToggle={toggleDeleteEmployeeOpen}
        employee={employee}
      />
      <EditEmployeeModal
        isOpen={isEditEmployeeOpen}
        onToggle={toggleEditEmployeeOpen}
        employee={employee}
      />
    </>
  );
};
